import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
  IconBuilding,
  IconCheck,
  IconWorld,
  IconLanguage,
  IconMail,
  IconRefresh,
  IconSparkles,
  IconTarget,
  IconUser,
} from "@tabler/icons-react";
import {
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Image,
  LoadingOverlay,
  Paper,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { AVATARS } from "./utils";

// --- COMPOSANT CONFETTI ---
function Confetti() {
  const [pieces] = useState(() =>
    Array.from({ length: 42 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.2 + Math.random() * 1.4,
      rotate: Math.random() * 360,
      color: [
        "var(--mantine-color-primary-5)",
        "var(--mantine-color-cyan-5)",
        "var(--mantine-color-orange-5)",
        "var(--mantine-color-grape-5)",
      ][i % 4],
    })),
  );

  return (
    <Box
      pos="absolute"
      inset={0}
      style={{ overflow: "hidden", pointerEvents: "none", zIndex: 50 }}
    >
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -40, x: `${p.x}vw`, opacity: 1, rotate: 0 }}
          animate={{ y: "110vh", rotate: p.rotate, opacity: [1, 1, 0] }}
          transition={{ duration: p.duration, delay: p.delay, ease: "easeIn" }}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 8,
            height: 8,
            borderRadius: 8,
            backgroundColor: p.color,
          }}
        />
      ))}
    </Box>
  );
}

// --- COMPOSANT ROW (Ligne d'information) ---
function Row({ icon, label, value }) {
  return (
    <Paper
      withBorder
      radius="xl"
      p="md"
      bg={"#fff"}
      style={{ height: "100%" }}
      shadow="none"
    >
      <Group wrap="nowrap" align="center" gap="md">
        <ThemeIcon size={40} radius="lg" variant="light" color="primary">
          {icon}
        </ThemeIcon>
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text size="md" fw={400} tt="uppercase" c="dimmed" lts={1}>
            {label}
          </Text>
          <Text size="lg" fw={500} mt={4}>
            {value}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
}

// --- COMPOSANT PRINCIPAL FINAL SCREEN ---
export function FinalScreen({ draft, onCreate, onRestart, created, creating }) {
  const avatar = AVATARS.find((a) => a.id === draft.avatarId);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (created) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 4200);
      return () => clearTimeout(t);
    }
  }, [created]);

  return (
    <Box pos="relative" maw={900} mx="auto" px="md" py="xl">
      <LoadingOverlay visible={creating} />
      {showConfetti && <Confetti />}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Paper
          radius={32}
          shadow="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* En-tête : Résumé et Avatar */}
          <Box p="xl" ta="center" bg={"#fff"}>
            <Box
              mx="auto"
              w={96}
              h={96}
              style={{
                borderRadius: 28,
                border: `2px solid ${
                  avatar
                    ? `var(--mantine-color-${avatar.accent}-5)`
                    : "var(--mantine-color-primary-5)"
                }`,
                backgroundColor: "var(--mantine-color-gray-1)",
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                boxShadow: "var(--mantine-shadow-md)",
              }}
            >
              {avatar ? (
                <Image
                  src={avatar.src || "/placeholder.svg"}
                  alt={draft.name}
                  w="100%"
                  h="100%"
                  fit="cover"
                />
              ) : (
                <IconUser size={40} color="var(--mantine-color-gray-5)" />
              )}
            </Box>

            <Box mt="md">
              <Badge
                variant="light"
                color="primary"
                size="md"
                radius="xl"
                leftSection={<IconSparkles size={12} />}
                style={{ textTransform: "none" }}
              >
                {created ? "Agent créé avec succès" : "Prêt à être créé"}
              </Badge>
              <Title
                order={2}
                mt="sm"
                fw={600}
                style={{ letterSpacing: "-0.5px" }}
              >
                {draft.name}
              </Title>
              <Text size="lg" mt={4} opacity={0.7}>
                {draft.role} IA · prospecte pour {draft.companyName}
              </Text>
            </Box>
          </Box>

          {/* Corps : Grille d'informations */}
          <Box p="xl" bg="white">
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Row
                  icon={<IconBuilding size={20} />}
                  label="Entreprise"
                  value={draft.companyName}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Row
                  icon={<IconUser size={20} />}
                  label="Rôle"
                  value={draft.role}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Row
                  icon={<IconLanguage size={20} />}
                  label="Langue"
                  value={draft.language}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Row
                  icon={<IconTarget size={20} />}
                  label="Promesse de valeur"
                  value={
                    draft.chosenValueProp?.split("\n").map((prop, index) => (
                      <React.Fragment key={index}>
                        • {prop}{" "}
                        {index <
                        draft.chosenValueProp.split("\n").length - 1 ? (
                          <br />
                        ) : (
                          ""
                        )}
                      </React.Fragment>
                    )) || "—"
                  }
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Row
                  icon={<IconWorld size={20} />}
                  label="Ton de communication"
                  value={draft.tone}
                />
              </Grid.Col>

              {/* Consignes */}
              <Grid.Col span={12}>
                <Paper withBorder radius="xl" p="lg" bg="#fff" shadow="none">
                  <Text size="md" fw={600} tt="uppercase" c="dimmed" lts={1}>
                    Consignes de l'agent
                  </Text>
                  <Text
                    size="lg"
                    mt="sm"
                    lh={1.6}
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {draft.instructions}
                  </Text>
                </Paper>
              </Grid.Col>
            </Grid>
          </Box>

          {/* Pied de page : Actions */}
          <Box
            p="xl"
            style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
          >
            <Group justify="space-between" align="center">
              <Button
                variant="default"
                radius="xl"
                size="lg"
                leftSection={<IconRefresh size={18} />}
                onClick={onRestart}
                style={{ border: "1px solid var(--mantine-color-gray-3)" }}
              >
                Recommencer
              </Button>

              <Button
                variant="gradient"
                gradient={{ from: "violet", to: "blue", deg: 45 }}
                radius="xl"
                size="lg"
                leftSection={
                  created ? <IconCheck size={18} /> : <IconSparkles size={18} />
                }
                onClick={onCreate}
                disabled={created}
                loading={creating}
                style={{
                  boxShadow: created ? "none" : "var(--mantine-shadow-md)",
                  transition: "all 0.2s ease",
                }}
              >
                {created ? "Agent créé" : "Créer mon agent"}
              </Button>
            </Group>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
}
